import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NAV_LINKS, CLINIC } from "@/data/site";
import { trackEvent } from "@/lib/analytics";
import { triggerLink } from "@/lib/actions";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 border-b ${
        scrolled
          ? "bg-[rgba(250,250,248,0.9)] backdrop-blur-md border-[color:var(--cdt-border)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-full bg-[var(--cdt-primary)] flex items-center justify-center text-white font-display text-lg">C</span>
            <span className="leading-tight">
              <span className="block font-display text-lg text-[var(--cdt-primary-2)]">Center Of Dental Treatment</span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[var(--cdt-muted)]">Greater Noida</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive ? "text-[var(--cdt-primary)] font-semibold" : "text-[var(--cdt-text)] hover:text-[var(--cdt-primary)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => { trackEvent("appointment_cta_click", { location: "navbar" }); navigate("/appointment"); }}
              data-testid="navbar-appointment-button"
              className="hidden sm:inline-flex bg-[var(--cdt-primary)] hover:bg-[var(--cdt-primary-2)] text-white rounded-[var(--cdt-radius-sm)]"
            >
              Request Appointment
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-nav-trigger" className="xl:hidden h-10 w-10 inline-flex items-center justify-center rounded-md border border-[color:var(--cdt-border)] bg-white">
                  <Menu className="h-5 w-5 text-[var(--cdt-text)]" />
                </button>
              </SheetTrigger>
              <SheetContent data-testid="mobile-nav-sheet" side="right" className="w-[85vw] sm:w-96 bg-[var(--cdt-bg)]">
                <div className="mt-8 flex flex-col gap-1">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.to}>
                      <NavLink
                        to={l.to}
                        data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
                        className={({ isActive }) =>
                          `px-4 py-3 rounded-lg text-base ${isActive ? "bg-white text-[var(--cdt-primary)] font-semibold" : "text-[var(--cdt-text)]"}`
                        }
                      >
                        {l.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Link to="/appointment" data-testid="mobile-nav-appointment" className="mt-4 px-4 py-3 rounded-lg bg-[var(--cdt-primary)] text-white text-center font-semibold">
                      Request Appointment
                    </Link>
                  </SheetClose>
                  <button onClick={() => triggerLink(CLINIC.phoneHref, false)} data-testid="mobile-nav-call" className="mt-2 px-4 py-3 rounded-lg border border-[color:var(--cdt-border)] flex items-center gap-2 text-[var(--cdt-text)] w-full">
                    <Phone className="h-4 w-4" /> {CLINIC.phone}
                  </button>
                  <button onClick={() => triggerLink(CLINIC.phone2Href, false)} data-testid="mobile-nav-call-2" className="px-4 py-3 rounded-lg border border-[color:var(--cdt-border)] flex items-center gap-2 text-[var(--cdt-text)] w-full">
                    <Phone className="h-4 w-4" /> {CLINIC.phone2}
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
